import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { UseFormSetError } from 'react-hook-form';
import { useProfile } from '../api';
import { authHeader, handleResponse } from '../queryClient';
import { ExerciseState, ExerciseType } from '../types';
import { asUTCString, formatDateTime } from '../utils/formatTime';
import { setFormFieldError } from '../utils/setFormFieldError';
import { useActiveCompany } from '../utils/useCompany';
import { AdHocExerciseFormData } from './ad-hoc/CreateAdHocExerciseDialog';
import { ExerciseFormData } from './scheduled/CreateExerciseDialog';

const exerciseFormFields: (keyof (ExerciseFormData | AdHocExerciseFormData))[] = [
  'description',
  'endTime',
  'exercises',
  'layout',
  'limitNumberOfAttempts',
  'maxNumberOfAttempts',
  'studentEnvironment',
  'simulator',
  'startTime',
  'timeRange',
  'timezone',
  'title',
  'type',
  'users',
  'vessel',
  'visibility',
];

export const makeCreateExerciseRequest = (
  data: Omit<ExerciseFormData | AdHocExerciseFormData, 'limitNumberOfAttempts'>,
  limitNumberOfAttempts: boolean,
  name?: string,
) => {
  const {
    adHoc,
    exerciseId,
    exercises,
    exercisesIds,
    exercisesMetaData,
    simulator,
    timezone,
    type,
    users,
    visibility,
  } = data;

  const ids = exercisesIds || [exerciseId];

  return {
    adHoc,
    description: data.description.trim() || `Simulate now exercise started on ${formatDateTime(new Date())} by ${name}`,
    endTime: asUTCString(data.endTime ?? undefined),
    exercises: ids.map(id => {
      const metaData = exercisesMetaData?.find(exercise => exercise.id === id)?.metaData[0] || null;

      const exercise = {
        id,
        title: `${data.title.trim() || `Simulate now exercise`} (${
          exercises?.find(exercise => exercise.id === id)?.title || ` `
        })`,
        layout: data.layout || null,
      };

      return type === ExerciseType.SelfStudy
        ? {
            ...exercise,
            position: data.studentEnvironment?.name || (metaData?.students && metaData.students[0].name) || null,
            vesselCode: data.vessel?.vesselCode || metaData?.vesselCode || null,
            vesselName: data.vessel?.vesselName || metaData?.vesselName || null,
          }
        : exercise;
    }),
    maxNumberOfAttempts: limitNumberOfAttempts ? +data.maxNumberOfAttempts : undefined,
    simulator,
    startTime: asUTCString(data.startTime ?? undefined),
    timezone,
    type,
    users,
    visibility,
  };
};

export const useCreateExercise = <T extends ExerciseFormData | (AdHocExerciseFormData & { adHoc: boolean })>(
  setFieldError: UseFormSetError<any>,
  onSuccess?: (id: string) => void,
) => {
  const [showCreateExerciseError, setShowCreateExerciseError] = useState(false);
  const [createExerciseError, setError] = useState<string>();

  const activeCompany = useActiveCompany();
  const queryClient = useQueryClient();
  const { profile } = useProfile();

  const { isLoading: isCreatingExercise, mutate: createExercise } = useMutation<{ id: string }, Response, T>({
    async mutationFn({ limitNumberOfAttempts, ...data }) {
      setShowCreateExerciseError(false);
      try {
        /* eslint-disable no-console */
        console.log('[createExerciseRequest]', {
          formData: data,
          limitNumberOfAttempts,
          profile,
          request: makeCreateExerciseRequest(data, limitNumberOfAttempts, profile?.name),
        });

        return fetch(`/api/teach/companies/${activeCompany?.companyId}/scheduled-exercises`, {
          body: JSON.stringify(makeCreateExerciseRequest(data, limitNumberOfAttempts, profile?.name)),
          headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
          method: 'POST',
        }).then(handleResponse);
      } catch (error) {
        /* eslint-disable no-console */
        console.error('[Error -> CreateExerciseRequest]', error);

        setShowCreateExerciseError(true);
      }
    },
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [
          {
            path: `teach/companies/${activeCompany?.companyId}/scheduled-exercises?scheduleState=${ExerciseState.Active}`,
          },
        ],
      });

      onSuccess && onSuccess(data.id);
    },
    onError: async error => {
      const body = await error.json();
      if (body.errors) {
        setFormFieldError(body.errors, exerciseFormFields, setFieldError);
      } else {
        setShowCreateExerciseError(true);
      }

      if (body.title ?? body.message) {
        setShowCreateExerciseError(true);
        setError(body.title ?? body.message);
      }
    },
  });

  return {
    createExerciseError,
    createExercise,
    isCreatingExercise,
    setError,
    setShowCreateExerciseError,
    showCreateExerciseError,
  };
};
